import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  FileText,
  Download,
  Loader2,
  Search,
  X,
  Settings2,
} from 'lucide-react';
import { FieldSelector } from '@/components/TimesheetReports/FieldSelector';
import type { FieldConfig, FieldPreset } from '@/components/TimesheetReports/FieldSelector';
import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { reportsApi } from '@/services/reportsApi';
import type { PagedTimesheetReportResponse, ReportSummaryDto } from '@/services/reportsApi';
import { rejectionsApi } from '@/services/rejectionsApi';
import { TimesheetReportGrid } from '@/components/TimesheetReportGrid';
import { AdvancedFilterDrawer } from '@/components/TimesheetReports/AdvancedFilterDrawer';
import { QuickFilters } from '@/components/TimesheetReports/QuickFilters';
import { ActiveFilterChips } from '@/components/TimesheetReports/ActiveFilterChips';
import { transformTimesheetsToReportEntries, getReportSummary } from '@/utils/timesheetReportUtils';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useOvertimeTypes } from '@/hooks/useOvertimeTypes';
import { useSettings } from '@/contexts/SettingsContext';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { toast } from 'sonner';

// Navigation state types
type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

type AppNavigationState = {
  timesheetDrilldown?: TimesheetDrilldownState;
};

// Main Timesheet Reports Page Component
interface TimesheetReportsPageProps {
  onNavigate?: (path: string, state?: AppNavigationState) => void;
  initialFilters?: TimesheetReportFilters;
}

export const TimesheetReportsPage: React.FC<TimesheetReportsPageProps> = ({ onNavigate, initialFilters }) => {
  const { timesheets, employees, projects, costCentres, currentEmployee } = useTimesheet();
  const { canExportReport, getReportViewAccessLevel } = usePermissions();
  const { organizationSettings } = useSettings();
  const enableOvertimeTracking = organizationSettings?.overtime?.enabled === true;

  // Fetch overtime types from the OvertimeTypes API instead of organizationSettings
  const { activeTypes: availableOvertimeTypes } = useOvertimeTypes({ enabled: enableOvertimeTracking, screenId: 'reports-timesheet' });
  
  // Advanced Filter state - initialize with passed filters if available
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filters, setFilters] = useState<TimesheetReportFilters>(initialFilters || {});
  const [searchQuery, setSearchQuery] = useState('');

  // Field selector state
  const [fieldPresets, setFieldPresets] = useState<FieldPreset[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [fieldConfiguration, setFieldConfiguration] = useState<FieldConfig[]>([]);

  const { mode, isDemo, isLive } = useScreenDataMode('reports-timesheet');
  const { currentCompany } = useCompany();

  const [liveReportData, setLiveReportData] = useState<PagedTimesheetReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update filters when initialFilters change (e.g., when navigating from dashboard)
  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  useEffect(() => {
    if (isLive) {
      fetchLiveReportData(filters);
    }
  }, [isLive, currentCompany?.id, filters]);

  // Listen for company-changed event to refresh data when the active company is switched
  useEffect(() => {
    const handleCompanyChange = () => {
      if (isLive) {
        fetchLiveReportData(filters);
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, [isLive, filters]);

  const fetchLiveReportData = async (currentFilters: TimesheetReportFilters) => {
    setLoading(true);
    setError(null);
    try {
      // Backend enforces access-level scoping based on the user's role permissions,
      // so we no longer hard-code employeeIds to only the current user.
      // Users with 'all' access will see all employees, 'team' will see direct reports,
      // and 'own' will only see their own data — all enforced server-side.
      const response = await reportsApi.getTimesheetReport({
        dateFrom: currentFilters.dateFrom,
        dateTo: currentFilters.dateTo,
        employeeIds: currentFilters.employeeIds,
        statuses: currentFilters.statuses,
        departments: currentFilters.departments,
        billableOnly: currentFilters.billableOnly,
        rejectedOnly: currentFilters.rejectedOnly,
        overtimeOnly: currentFilters.overtimeOnly,
        overtimeTypeIds: currentFilters.overtimeTypeIds,
        includeSummary: true,
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'weekEnding',
        sortDir: 'desc',
        companyId: currentCompany?.id,
      });
      setLiveReportData(response);
    } catch (err) {
      const errorMessage = 'Failed to load report data from API';
      setError(errorMessage);
      console.error('Error fetching live report data:', err);
      toast.error('Failed to load report data', {
        description: 'Could not connect to the API. Please ensure the backend is running or switch to demo mode.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine if user is a manager (has 'all' or 'team' access level)
  const isManager = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('timesheet');
    return accessLevel === 'all' || accessLevel === 'team';
  }, [getReportViewAccessLevel]);

  // Get timesheets filtered by access level permission
  const allTimesheets = useMemo(() => {
    const accessLevel = getReportViewAccessLevel('timesheet');
    
    let filteredTimesheets = timesheets;
    if (accessLevel === 'own' && currentEmployee) {
      // Only show current employee's timesheets
      filteredTimesheets = timesheets.filter(ts => ts.employeeId === currentEmployee.id);
    } else if (accessLevel === 'team' && currentEmployee) {
      // Show current employee's and their direct reports' timesheets
      const teamMemberIds = employees
        .filter(emp => emp.managerId === currentEmployee.id || emp.id === currentEmployee.id)
        .map(emp => emp.id);
      filteredTimesheets = timesheets.filter(ts => teamMemberIds.includes(ts.employeeId));
    }
    // 'all' access level shows all timesheets (no filtering)
    
    return filteredTimesheets.sort((a, b) => b.weekEnding.getTime() - a.weekEnding.getTime());
  }, [timesheets, currentEmployee, employees, getReportViewAccessLevel]);

  // Transform timesheets to report entries (demo mode)
  const demoReportEntries = useMemo(() => {
    return transformTimesheetsToReportEntries(allTimesheets, employees, projects);
  }, [allTimesheets, employees, projects]);

  // Use live data when available, otherwise use demo data
  const reportEntries = useMemo(() => {
    if (isLive && liveReportData) {
      return liveReportData.items;
    }
    return demoReportEntries;
  }, [isLive, liveReportData, demoReportEntries]);

  // Apply client-side filters to report entries.
  // In live mode, most filters (date range, employees, statuses, departments,
  // billable, rejected, overtime) are already applied server-side by the API.
  // Client-side filtering is still needed for: text search (purely local),
  // overtime-type-name matching (uses local availableOvertimeTypes lookup),
  // and all filters in demo mode.
  const filteredReportEntries = useMemo(() => {
    try {
      return reportEntries.filter((entry) => {
        try {
          // Search filter (always client-side)
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const searchableFields = [
              entry.employeeName,
              entry.department,
              entry.status,
              entry.projectCode,
              entry.projectName,
              entry.costCentreCode,
              entry.costCentreName,
              entry.description || '',
            ].map(field => String(field).toLowerCase());
            
            if (!searchableFields.some(field => field.includes(query))) {
              return false;
            }
          }

          // In live mode the API already filters by date, employee, status,
          // department, billable, rejected, overtime, and project — so skip
          // those checks to avoid redundant work.
          if (isLive && liveReportData) {
            // Overtime type filter still needs client-side matching because
            // the API filters by overtimeTypeId but the grid only has the
            // overtime type *name*, so we match via availableOvertimeTypes.
            if (filters.overtimeTypeIds && filters.overtimeTypeIds.length > 0) {
              if (!entry.overtimeTypeName) return false;
              const matchedType = availableOvertimeTypes.find(
                (t) => t.name === entry.overtimeTypeName
              );
              if (!matchedType || !filters.overtimeTypeIds!.includes(matchedType.id)) return false;
            }
            return true;
          }

          // --- Demo mode: apply all filters client-side ---
          
          // Date range filter
          if (filters.dateFrom) {
            const fromDate = filters.dateFrom instanceof Date ? filters.dateFrom : new Date(filters.dateFrom);
            if (isNaN(fromDate.getTime())) {
              console.warn('Invalid dateFrom:', filters.dateFrom);
              return true; // Skip this filter if date is invalid
            }
            if (entry.weekEnding < fromDate) return false;
          }
          if (filters.dateTo) {
            const toDate = filters.dateTo instanceof Date ? filters.dateTo : new Date(filters.dateTo);
            if (isNaN(toDate.getTime())) {
              console.warn('Invalid dateTo:', filters.dateTo);
              return true; // Skip this filter if date is invalid
            }
            if (entry.weekEnding > toDate) return false;
          }
          
          // Employee filter
          if (filters.employeeIds && filters.employeeIds.length > 0 && 
              !filters.employeeIds.includes(entry.employeeId)) return false;
          
          // Status filter
          if (filters.statuses && filters.statuses.length > 0 && 
              !filters.statuses.includes(entry.status)) return false;
          
          // Department filter
          if (filters.departments && filters.departments.length > 0 && 
              !filters.departments.includes(entry.department)) return false;
          
          // Project filter (using projectCode as identifier)
          if (filters.projectIds && filters.projectIds.length > 0 && 
              !filters.projectIds.includes(entry.projectCode)) return false;
          
          // Billable filter
          if (filters.billableOnly === true && entry.billable !== true) return false;
          
          // Overtime filter
          if (filters.overtimeOnly === true && entry.isOvertime !== true) return false;

          // Overtime type filter
          if (filters.overtimeTypeIds && filters.overtimeTypeIds.length > 0) {
            if (!entry.overtimeTypeName) return false;
            const matchedType = availableOvertimeTypes.find(
              (t) => t.name === entry.overtimeTypeName
            );
            if (!matchedType || !filters.overtimeTypeIds!.includes(matchedType.id)) return false;
          }
          
          // Rejected filter
          if (filters.rejectedOnly && !entry.hasRejections) return false;
          
          return true;
        } catch (entryError) {
          console.error('Error filtering entry:', entryError, entry);
          return true; // Include entry if there's an error filtering it
        }
      });
    } catch (error) {
      console.error('Error in filteredReportEntries:', error);
      toast.error('Error filtering report data');
      return reportEntries; // Return unfiltered data if there's an error
    }
  }, [reportEntries, filters, searchQuery, availableOvertimeTypes, isLive, liveReportData]);

  // Get report summary (based on all data - filtering will be handled by grid)
  const reportSummary = useMemo(() => {
    if (isLive && liveReportData?.summary) {
      return liveReportData.summary;
    }
    return getReportSummary(demoReportEntries);
  }, [isLive, liveReportData, demoReportEntries]);

  // Handle row click to navigate to timesheet detail
  const handleRowClick = (entry: any) => {
    if (onNavigate) {
      // Navigate to Timesheet Grid with the selected employee and week
      onNavigate('/timesheet', {
        timesheetDrilldown: {
          employeeId: entry.employeeId,
          weekEnding: entry.weekEnding
        }
      });
    }
  };

  // Handle Excel export
  const handleExport = () => {
    try {
      // Build data rows
      const headers = [
        'Employee',
        'Week Ending',
        'Status',
        'Project',
        'Cost Centre',
        'Description',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
        'Total Hours',
        'Billable',
        ...(enableOvertimeTracking ? ['OT', 'Overtime Type'] : []),
      ];

      const rows = reportEntries.map(entry => [
        entry.employeeName,
        format(entry.weekEnding, 'yyyy-MM-dd'),
        entry.status,
        entry.projectName,
        entry.costCentreName,
        entry.description || '',
        entry.mondayHours,
        entry.tuesdayHours,
        entry.wednesdayHours,
        entry.thursdayHours,
        entry.fridayHours,
        entry.saturdayHours,
        entry.sundayHours,
        entry.totalHours,
        entry.billable ? 'Yes' : 'No',
        ...(enableOvertimeTracking ? [
          entry.isOvertime ? 'Yes' : 'No',
          entry.overtimeTypeName ?? '',
        ] : []),
      ]);

      // Summary section
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Hours', reportSummary.totalHours]);
      rows.push(['Billable Hours', reportSummary.billableHours]);
      rows.push(['Billable %', `${reportSummary.billablePercentage.toFixed(1)}%`]);
      rows.push(['Total Entries', reportSummary.totalEntries]);
      rows.push(['Unique Projects', reportSummary.uniqueProjects]);
      rows.push(['Entries with Rejections', reportSummary.entriesWithRejections]);

      // Build worksheet
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Auto-size columns
      const colWidths = headers.map((h, i) => {
        const maxLen = Math.max(
          h.length,
          ...rows.map(r => String(r[i] ?? '').length)
        );
        return { wch: Math.min(maxLen + 2, 40) };
      });
      worksheet['!cols'] = colWidths;

      // Create workbook and download
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Timesheet Report');
      XLSX.writeFile(workbook, `timesheet-report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);

      toast.success('Export Successful', {
        description: `Exported ${reportEntries.length} timesheet entries to Excel`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export Failed', {
        description: 'There was an error exporting the report',
      });
    }
  };

  // Prepare data for Advanced Filter Dialog
  const availableEmployees = useMemo(() => {
    return employees.map(emp => ({
      id: emp.id,
      name: emp.name,
      department: emp.department,
    }));
  }, [employees]);

  const availableDepartments = useMemo(() => {
    return Array.from(new Set(employees.map(emp => emp.department))).sort();
  }, [employees]);

  const availableProjects = useMemo(() => {
    return projects.map(proj => ({
      id: proj.id,
      code: proj.code,
      name: proj.name,
    }));
  }, [projects]);

  const availableCostCentres = useMemo(() => {
    return costCentres.map(cc => ({
      id: cc.id,
      code: cc.code,
      name: cc.name,
    }));
  }, [costCentres]);

  // Count active filters (only from advanced drawer, not quick filters)
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    // Only count filters that are NOT in quick filters
    if (filters.departments && filters.departments.length > 0) count++;
    if (filters.billableOnly) count++;
    if (filters.overtimeOnly) count++;
    if (filters.overtimeTypeIds && filters.overtimeTypeIds.length > 0) count++;
    if (filters.rejectedOnly) count++;
    // Count date range only if it's a custom range (not from quick filter)
    if ((filters.dateFrom || filters.dateTo) && !filters.quickDateRange) count++;
    return count;
  }, [filters]);

    // Handle removing individual filter
    const handleRemoveFilter = (filterKey: string, value?: string | number) => {
      const newFilters = { ...filters };
    
      switch (filterKey) {
        case 'dateRange':
          delete newFilters.dateFrom;
          delete newFilters.dateTo;
          delete newFilters.quickDateRange;
          break;
        case 'statuses':
          if (value && newFilters.statuses) {
            newFilters.statuses = newFilters.statuses.filter(s => s !== value);
            if (newFilters.statuses.length === 0) delete newFilters.statuses;
          }
          break;
        case 'employeeIds':
          if (value !== undefined && newFilters.employeeIds) {
            const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
            newFilters.employeeIds = newFilters.employeeIds.filter(id => id !== numValue);
            if (newFilters.employeeIds.length === 0) delete newFilters.employeeIds;
          }
          break;
      case 'departments':
        if (value && newFilters.departments) {
          newFilters.departments = newFilters.departments.filter(d => d !== value);
          if (newFilters.departments.length === 0) delete newFilters.departments;
        }
        break;
      case 'projectIds':
        if (value && newFilters.projectIds) {
          newFilters.projectIds = newFilters.projectIds.filter(id => id !== value);
          if (newFilters.projectIds.length === 0) delete newFilters.projectIds;
        }
        break;
      case 'billableOnly':
        delete newFilters.billableOnly;
        break;
      case 'overtimeOnly':
        delete newFilters.overtimeOnly;
        break;
      case 'overtimeTypeIds':
        if (value && newFilters.overtimeTypeIds) {
          newFilters.overtimeTypeIds = newFilters.overtimeTypeIds.filter((id) => id !== value);
          if (newFilters.overtimeTypeIds.length === 0) delete newFilters.overtimeTypeIds;
        }
        break;
      case 'rejectedOnly':
        delete newFilters.rejectedOnly;
        break;
      case 'groupBy':
        if (value && newFilters.groupBy) {
          newFilters.groupBy = newFilters.groupBy.filter(g => g !== value);
          if (newFilters.groupBy.length === 0) delete newFilters.groupBy;
        }
        break;
    }
    
    setFilters(newFilters);
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setFilters({});
  };

  // Field configuration for FieldSelector
  const storageKey = 'timesheet-report-employee';
  
  // Load field presets from localStorage
  useEffect(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(`field-presets-${storageKey}`);
        if (saved) {
          setFieldPresets(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Failed to load field presets:', error);
      }
    }
  }, [storageKey]);

  // Define field labels (conditionally include department for managers)
  const fieldLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {
      weekEnding: 'Week Ending',
      employeeName: 'Employee',
      status: 'Status',
      projectCode: 'Project Code',
      projectName: 'Project Name',
      costCentreCode: 'Cost Centre',
      description: 'Description',
      mondayHours: 'Monday',
      tuesdayHours: 'Tuesday',
      wednesdayHours: 'Wednesday',
      thursdayHours: 'Thursday',
      fridayHours: 'Friday',
      saturdayHours: 'Saturday',
      sundayHours: 'Sunday',
      totalHours: 'Total Hours',
      billable: 'Billable',
      hasRejections: 'Rejections',
    };
    
    // Only include department field for managers
    if (isManager) {
      labels.department = 'Department';
    }
    
    return labels;
  }, [isManager]);

  const fieldCategories: Record<string, string> = useMemo(() => {
    const categories: Record<string, string> = {
      weekEnding: 'Time Period',
      employeeName: 'People',
      status: 'Status',
      projectCode: 'Project',
      projectName: 'Project',
      costCentreCode: 'Finance',
      description: 'Details',
      mondayHours: 'Daily Hours',
      tuesdayHours: 'Daily Hours',
      wednesdayHours: 'Daily Hours',
      thursdayHours: 'Daily Hours',
      fridayHours: 'Daily Hours',
      saturdayHours: 'Daily Hours',
      sundayHours: 'Daily Hours',
      totalHours: 'Summary',
      billable: 'Finance',
      hasRejections: 'Status',
    };
    
    // Only include department category for managers
    if (isManager) {
      categories.department = 'People';
    }
    
    return categories;
  }, [isManager]);

  // Create default field configs (always in default order)
  const defaultFieldConfigs: FieldConfig[] = useMemo(() => {
    const allFields = Object.keys(fieldLabels);
    return allFields.map((id, index) => ({
      id,
      label: fieldLabels[id],
      visible: true, // All visible by default
      order: index,
      category: fieldCategories[id],
    }));
  }, [fieldLabels, fieldCategories]);

  // Create field configs (reflects current state)
  const fieldConfigs: FieldConfig[] = useMemo(() => {
    // If we have a saved field configuration, use its order
    // But filter out any fields that are no longer available (e.g., department for non-managers)
    if (fieldConfiguration.length > 0) {
      return fieldConfiguration
        .filter((field) => fieldLabels[field.id]) // Only include fields that exist in fieldLabels
        .map((field) => ({
          ...field,
          visible: columnVisibility[field.id] !== false,
        }));
    }
    
    // Otherwise, use default order
    return defaultFieldConfigs.map((field) => ({
      ...field,
      visible: columnVisibility[field.id] !== false,
    }));
  }, [columnVisibility, fieldConfiguration, fieldLabels, defaultFieldConfigs]);

  const handleFieldsChange = (fields: FieldConfig[]) => {
    const newVisibility: Record<string, boolean> = {};
    fields.forEach((field) => {
      newVisibility[field.id] = field.visible;
    });
    setColumnVisibility(newVisibility);
    setFieldConfiguration(fields);
  };

  const handleSavePreset = (preset: FieldPreset) => {
    const newPresets = [...fieldPresets, preset];
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(`field-presets-${storageKey}`, JSON.stringify(newPresets));
    }
  };

  const handleDeletePreset = (presetId: string) => {
    const newPresets = fieldPresets.filter((p) => p.id !== presetId);
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(`field-presets-${storageKey}`, JSON.stringify(newPresets));
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Timesheet Reports"
          subtitle="Comprehensive timesheet analysis and reporting"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<FileText />}
        />
        <div className="flex items-center gap-4 ml-auto flex-shrink-0">
          <FeatureFeedbackCollector feature="Timesheet Report" />
        </div>
      </div>

      {/* Data Grid */}
      <Card>
        <CardContent className="pt-6 pb-3 px-6">
          {/* Top level layout: Search, Filters, Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4 -mx-6 px-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search timesheets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <div className="lg:flex-shrink-0">
              <QuickFilters
              filters={filters}
              onFiltersChange={setFilters}
              onAdvancedClick={() => setShowAdvancedFilter(true)}
              advancedFilterCount={advancedFilterCount}
              customActions={
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={handleExport}
                    disabled={!canExportReport('timesheet')}
                    title={!canExportReport('timesheet') ? "You don't have permission to export timesheet reports" : "Export to Excel"}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <FieldSelector
                    fields={fieldConfigs}
                    defaultFields={defaultFieldConfigs}
                    onFieldsChange={handleFieldsChange}
                    storageKey={storageKey}
                    presets={fieldPresets}
                    onSavePreset={handleSavePreset}
                    onDeletePreset={handleDeletePreset}
                  />
                </>
              }
            />
            </div>
          </div>

          {/* Active Filter Chips */}
          <ActiveFilterChips
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            availableEmployees={availableEmployees}
            availableProjects={availableProjects}
            availableDepartments={availableDepartments}
            availableOvertimeTypes={availableOvertimeTypes}
          />



          {isLive && loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading report data...</p>
              </div>
            </div>
          )}
          
          {isLive && error && !loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                <Button onClick={() => fetchLiveReportData(filters)} variant="outline" size="sm">
                  Retry
                </Button>
              </div>
            </div>
          )}
          
          {(!isLive || (!loading && !error)) && (
            <TimesheetReportGrid
              data={filteredReportEntries}
              isManager={isManager}
              enableOvertimeTracking={enableOvertimeTracking}
              onRowClick={handleRowClick}
              columnVisibility={columnVisibility}
              fieldConfig={fieldConfiguration.length > 0 ? fieldConfiguration : undefined}
              groupBy={filters.groupBy}
            />
          )}
        </CardContent>
      </Card>

      {/* Advanced Filter Drawer */}
      <AdvancedFilterDrawer
        open={showAdvancedFilter}
        onOpenChange={setShowAdvancedFilter}
        filters={filters}
        onFiltersChange={setFilters}
        availableEmployees={availableEmployees}
        availableDepartments={availableDepartments}
        availableProjects={availableProjects}
        availableCostCentres={availableCostCentres}
        enableOvertimeTracking={enableOvertimeTracking}
        availableOvertimeTypes={availableOvertimeTypes}
      />
    </div>
  );
};
